import { useState, useEffect } from "react";

const niqsLogo = "/niqs-logo.png";

/** Loads a cross-origin image URL and returns it as a base64 data URL.
 *  Falls back to the original URL if CORS blocks the canvas read. */
function useEmbeddedImage(url: string | null | undefined): string | null {
  const [src, setSrc] = useState<string | null>(url ?? null);

  useEffect(() => {
    if (!url) { setSrc(null); return; }
    setSrc(url); // show immediately while converting

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width  = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d")!.drawImage(img, 0, 0);
        setSrc(canvas.toDataURL("image/jpeg"));
      } catch {
        // Canvas tainted (server missing CORS headers) — keep direct URL
      }
    };
    img.onerror = () => { /* keep direct URL */ };
    img.src = url;
  }, [url]);

  return src;
}

interface ExamColumn { id: string; code: string; title: string }

interface CardData {
  applicant: {
    name: string;
    exam_stage: string;
    exam_code: string;
    center: string;
    /**
     * Where the centre actually is — street and state, joined by the server.
     *
     * Null where the register holds no address for it, and the row is dropped
     * rather than printed empty: a card reading "CENTRE ADDRESS: —" tells a
     * candidate less than one that does not raise the question.
     */
    center_address?: string | null;
    exam_no: string;
    membership_no?: string | null;
    photo_url?: string | null;
    status: string;
  };
  session: {
    title: string;
    year: number;
    session_number: number;
  } | null;
  exam_columns: ExamColumn[];              // dynamic: [TPC, GDE, Intermediate, ...]
  timetable: Record<string, Record<string, {  // date -> slotKey -> { start, end, subjects }
    start_time: string;
    end_time: string;
    subjects: Record<string, string>;     // exam_id -> subject text
  }>>;
}

function fmtDate(dateStr: string): { day: string; date: string } {
  if (!dateStr) return { day: "", date: "" };
  // Parse only the date portion (YYYY-MM-DD) and construct at local midnight.
  // new Date("YYYY-MM-DD") is treated as UTC midnight by the spec, which shifts
  // the displayed day for users whose device timezone is behind UTC.
  const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
  const d = new Date(year, month - 1, day);
  return {
    day:  d.toLocaleDateString("en-NG", { weekday: "long" }).toUpperCase(),
    date: d.toLocaleDateString("en-NG", { day: "2-digit", month: "2-digit", year: "numeric" }),
  };
}

interface Props { data: any }

export default function ExamAdmissionCard({ data }: Props) {
  const applicant = (data?.applicant ?? null) as CardData["applicant"] | null;

  // Embed photo as base64 so it survives cross-origin restrictions during print.
  // Hook must run unconditionally (before any early return).
  const embeddedPhoto = useEmbeddedImage(applicant?.photo_url);

  if (!data || !applicant) {
    return (
      <div className="bg-white border-2 border-slate-200 p-16 text-center" style={{ width: "210mm" }}>
        <p className="text-slate-400 font-bold text-sm uppercase tracking-widest">Loading card…</p>
      </div>
    );
  }
  const session   = (data.session ?? null) as CardData["session"];

  // Guard against null/undefined/array responses when no timetable is set up yet
  const exam_columns: ExamColumn[] = Array.isArray(data.exam_columns) ? data.exam_columns : [];
  const rawTimetable = data.timetable;
  const timetable: CardData["timetable"] =
    rawTimetable && !Array.isArray(rawTimetable) && typeof rawTimetable === "object"
      ? rawTimetable
      : {};

  const sortedDates = Object.keys(timetable).sort();

  return (
    <div
      id="exam-admission-card"
      className="bg-white border-2 border-slate-900 font-sans"
      style={{ width: "210mm", minHeight: "297mm", padding: "10mm", boxSizing: "border-box" }}
    >
      {/* ── Header ── */}
      <div className="flex items-center gap-4 border-b-2 border-slate-900 pb-3 mb-3">
        <div className="flex-shrink-0">
          <img src={niqsLogo} alt="NIQS" className="h-24 w-24 object-contain"
            onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
        </div>
        <div className="flex-1 text-center">
          <h1 className="text-xl font-black tracking-wide leading-tight">
            THE NIGERIAN INSTITUTE OF<br />QUANTITY SURVEYORS
          </h1>
          <p className="text-[9px] text-slate-600 mt-0.5">
            No. 24, NIQS Crescent, Off Michael Ama Nnachi Crescent, Cadastral Zone B6, Mabushi District, Abuja
          </p>
          <p className="text-[9px] text-slate-600">
            Postal Address: P. O. Box 10689, Garki, Abuja &nbsp;|&nbsp; Tel: 08028303346, 08167593807 &nbsp;|&nbsp; info@niqsng.org
          </p>
        </div>
      </div>

      {/* ── Title ── */}
      <div className="text-center mb-4">
        <h2 className="text-base font-black tracking-widest uppercase underline">
          {session?.year ?? ""} PROFESSIONAL EXAMINATIONS
        </h2>
      </div>

      {/* ── Candidate details + photo ── */}
      <div className="flex gap-4 mb-4">
        <div className="flex-1 space-y-2">
          {[
            { label: "NAME:",       value: applicant?.name },
            { label: "EXAM STAGE:", value: applicant?.exam_stage },
            { label: "CENTRE:",     value: applicant?.center },
            // Straight after the centre it belongs to, and only when there is
            // one — see the note on the field. An older API that does not send
            // it leaves the card exactly as it was.
            ...(applicant?.center_address
              ? [{ label: "ADDRESS:", value: applicant.center_address }]
              : []),
            { label: "EXAM NO:",    value: applicant?.exam_no },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-start gap-2 border-b border-slate-200 pb-1">
              <span className="text-xs font-black uppercase tracking-widest w-28 flex-shrink-0">{label}</span>
              <span className="text-xs font-bold text-slate-800">{value || "—"}</span>
            </div>
          ))}
        </div>
        <div className="flex-shrink-0 w-28 h-36 border-2 border-slate-900 overflow-hidden bg-slate-100 flex items-center justify-center">
          {embeddedPhoto
            ? <img src={embeddedPhoto} alt={applicant?.name ?? ""} className="w-full h-full object-cover" />
            : <span className="text-[9px] text-slate-400 text-center font-bold px-1">PASSPORT PHOTO</span>
          }
        </div>
      </div>

      {/* ── Timetable ── */}
      <div className="mb-4">
        <h3 className="text-xs font-black uppercase tracking-widest text-center mb-2">
          {session?.title ?? `${session?.year ?? ""} PROFESSIONAL EXAMINATIONS TIME TABLE`}
        </h3>

        <table className="w-full border-collapse border border-slate-900 text-[9px]">
          <thead>
            <tr className="bg-slate-100">
              <th className="border border-slate-900 px-2 py-1.5 font-black uppercase text-left w-24">DATE</th>
              <th className="border border-slate-900 px-2 py-1.5 font-black uppercase text-left w-24">TIME</th>
              {/* Dynamic exam columns from API */}
              {exam_columns.map(col => (
                <th key={col.id} className="border border-slate-900 px-2 py-1.5 font-black uppercase text-center">
                  {col.title}<br/>
                  <span className="font-black text-slate-700">({col.code})</span>
                </th>
              ))}
              {exam_columns.length === 0 && (
                <th className="border border-slate-900 px-2 py-1.5 text-center text-slate-400">
                  No subjects entered
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedDates.map(date => {
              const slots = timetable[date] ?? {};
              const slotKeys = Object.keys(slots);
              const { day, date: dateLabel } = fmtDate(date);
              return slotKeys.map((slotKey, slotIdx) => {
                const slot = slots[slotKey];
                return (
                  <tr key={`${date}-${slotKey}`}>
                    {slotIdx === 0 && (
                      <td className="border border-slate-900 px-2 py-1.5 font-black align-top leading-tight" rowSpan={slotKeys.length}>
                        <div>{day}</div>
                        <div>{dateLabel}</div>
                      </td>
                    )}
                    <td className="border border-slate-900 px-2 py-1.5 font-bold whitespace-nowrap align-middle">
                      {slot.start_time}–{slot.end_time}
                    </td>
                    {exam_columns.map(col => (
                      <td key={col.id} className="border border-slate-900 px-2 py-1.5 align-middle leading-snug">
                        {slot.subjects?.[col.id] ?? ""}
                      </td>
                    ))}
                  </tr>
                );
              });
            })}
            {sortedDates.length === 0 && (
              <tr>
                <td colSpan={2 + exam_columns.length} className="border border-slate-900 px-2 py-4 text-center text-slate-400">
                  Timetable not yet published
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Footer ── */}
      <div className="text-center mt-4">
        <p className="text-xs font-black uppercase tracking-widest border border-slate-900 inline-block px-4 py-1">
          NB: ALWAYS BRING THIS CARD TO EXAMINATION HALL
        </p>
      </div>
    </div>
  );
}
